import { useEffect, useRef } from 'react'

/**
 * App used to carry nine copies of the same effect:
 *
 *   useEffect(=> {
 *     if (isLoading) return
 *     saveThing(value).catch(=> setToastMessage('Failed to save …'))
 *   }, [value, isLoading])
 *
 * differing only in the storage function, the extra scope the value is written under (the
 * gameweek), and the toast string. This is that shape, once.
 */
export interface AutosaveOptions<T> {
  /** The value to persist. A save fires whenever this changes identity. */
  value: T
  /**
   * Extra scope the value is written under — the gameweek, for the slices whose storage key
   * includes it. Changing it re-fires the save under the new scope, exactly as listing
   * `currentGameweek` in the inline dependency arrays did.
   */
  scope?: string | number
  /**
   * Whether saving is allowed at all — always `!isLoading`, sometimes also a plain state check.
   * Flipping it to true fires a save, which is what makes the end of the initial load write the
   * freshly-loaded state back, same as before.
   */
  enabled: boolean
  /**
   * Extra condition checked *inside* the effect rather than during render, so it can read a ref.
   * That's how the slices that reload on a gameweek change (`questPicks`, `hasStartedGw`) refuse
   * to write a still-stale value under an already-advanced gameweek number. It does not itself
   * schedule a save — like the inline `if (ref.current !== gw) return` it replaces, it is only
   * consulted when `value`/`scope`/`enabled` change.
   */
  guard?: () => boolean
  save: (value: T) => Promise<unknown>
  /** Called on a rejected save. Omitted where a failure is genuinely non-fatal. */
  onError?: () => void
  /**
   * Called after a save resolves, but only for a save the *user* caused.
   *
   * Most saves this hook fires are echoes: `enabled` flipping true at the end of the initial load
   * writes the freshly-loaded value straight back, and a gameweek change re-fires under the new
   * scope with the value that commit just set. Neither is somebody doing something. So the first
   * save under any given scope, and any save whose value is identical to the last one saved under
   * that scope, are silent — which also keeps StrictMode's double-mount from reporting one.
   */
  onSaved?: () => void
}

export function useAutosave<T>({
  value,
  scope,
  enabled,
  guard,
  save,
  onError,
  onSaved,
}: AutosaveOptions<T>): void {
  // These callbacks close over fresh render state and would be new functions every render, so
  // they are read through a ref rather than listed as dependencies. The ref-sync effect is
  // declared first, so it has already run by the time the save effect below reads it in the same
  // commit — the callbacks a save sees are always the current render's.
  const latest = useRef({ guard, save, onError, onSaved })
  useEffect(() => {
    latest.current = { guard, save, onError, onSaved }
  })

  /** What was last handed to `save`, and under which scope — the two things that separate a
   * user-caused save from an echo. See `onSaved`. */
  const lastSaved = useRef<{ scope: AutosaveOptions<T>['scope']; value: T } | null>(null)

  useEffect(() => {
    if (!enabled) return
    const { guard: check, save: persist, onError: handleError } = latest.current
    if (check && !check()) return
    const previous = lastSaved.current
    const isEcho =
      previous === null || previous.scope !== scope || Object.is(previous.value, value)
    lastSaved.current = { scope, value }
    persist(value)
      .then(() => {
        if (!isEcho) latest.current.onSaved?.()
      })
      .catch(() => handleError?.())
  }, [value, scope, enabled])
}
